import { NxtGlobalModalKey, useGlobalModal } from '@nxtpeople/nxt-styles';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { LocationQuery, useRoute } from 'vue-router';
import { notifySuccess } from '../helpers/notification.helper';
import { ApiResource } from '../models/ApiResource.interface';
import { useQueryWatcher } from './useQueryWatcher';

export interface GenericListActions<DataType, DataTypeDTO> {
   getAll?: (query?: LocationQuery) => Promise<ApiResource<Array<DataType>>>;
   delete?: (id: number, item?: DataType) => Promise<any>;
   create?: (data: DataTypeDTO) => Promise<DataType>;
   change?: (data: DataTypeDTO) => Promise<DataType>;
}

interface Params<DataType, DataTypeDTO> {
   routeName?: string;
   translation: (length?: number) => string;
   actions?: GenericListActions<DataType, DataTypeDTO>;
   autoFilter?: boolean;
}

export function useGenericList<DataType extends { id: number }, DataTypeDTO = DataType>(params: Params<DataType, DataTypeDTO>) {
   const { routeName, actions, autoFilter = true, translation } = params;
   const route = useRoute();
   const { t } = useI18n();
   const lastUsedQuery = ref<LocationQuery>({});
   const { openModal } = useGlobalModal();

   //#region GET
   const loading = ref(autoFilter);
   const overview = ref<ApiResource<Array<DataType>>>();

   if (autoFilter && routeName) {
      useQueryWatcher(routeName, getData);
   }

   async function getData(query?: LocationQuery): Promise<ApiResource<Array<DataType>>> {
      if (!actions?.getAll) throw Error('Get method not provided');
      loading.value = true;

      try {
         const response = await actions.getAll({ per_page: '25', ...query });
         overview.value = JSON.parse(JSON.stringify(response));
         lastUsedQuery.value = JSON.parse(JSON.stringify(query || {}));
         return response;
      } catch (error) {
         console.warn('Failed to load generic list data:', error);
         return overview.value ?? ({ data: [] } as ApiResource<Array<DataType>>);
      } finally {
         loading.value = false;
      }
   }
   //#endregion

   //#region CREATE
   const loadingCreate = ref(false);

   async function createData(data: DataTypeDTO, done: (response: DataType) => void): Promise<DataType> {
      loadingCreate.value = true;

      if (!actions?.create) throw Error('Create method not provided');

      return await actions
         .create(data)
         .then((response) => {
            done(response);
            notifySuccess({ title: t('generic.feedback.item-created', { name: translation() }) });
            overview.value?.data.push({ ...response });
            return response;
         })
         .finally(() => (loadingCreate.value = false));
   }
   //#endregion

   //#region PUT
   const loadingChange = ref(false);

   async function changeData(data: DataTypeDTO, done?: (response: DataType) => void): Promise<DataType> {
      loadingChange.value = true;

      if (!actions?.change) throw Error('Change method not provided');

      return await actions
         .change(data)
         .then((response) => {
            if (done) done(response);
            notifySuccess({ title: t('generic.feedback.item-changed', { name: translation() }) });
            const index = (overview.value?.data || []).findIndex((d) => d.id === response.id);
            if (index > -1) overview.value?.data.splice(index, 1, { ...response });
            return response;
         })
         .finally(() => (loadingChange.value = false));
   }

   //#endregion

   //#region DELETE
   const loadingDelete = ref(false);
   function deleteData({ itemToDelete }: { itemToDelete: DataType }): Promise<void> {
      return new Promise((resolve) => {
         openModal(NxtGlobalModalKey.CONFIRMATION, {
            title: t('generic.feedback.delete-item', { name: translation() }),
            description: t('generic.feedback.confirm-delete-item', { name: translation() }),
            cancelText: t('actions.cancel'),
            confirmText: t('actions.delete'),
            confirm: async () => {
               loadingDelete.value = true;
               if (!actions?.delete) throw Error('Delete method not provided');
               await actions
                  .delete(itemToDelete.id, itemToDelete)
                  .catch((err) => {
                     // 404 means the item is already gone — treat as a successful delete
                     if (err?.status !== 404) throw err;
                  })
                  .then(() => {
                     getData(lastUsedQuery.value);
                     notifySuccess({ title: t('generic.feedback.type-deleted', { type: translation() }) });
                     const index = (overview.value?.data || []).findIndex((d) => d.id === itemToDelete.id);
                     if (index > -1) overview.value?.data.splice(index, 1);
                  })
                  .finally(() => {
                     loadingDelete.value = false;
                     resolve();
                  });
            },
         });
      });
   }

   function deleteMultipleData(params: { itemsToDelete: Array<DataType>; done?: () => void }) {
      const { itemsToDelete, done } = params;
      const count = itemsToDelete.length;

      openModal(NxtGlobalModalKey.CONFIRMATION, {
         title: t('generic.feedback.delete-item', { name: translation(count) }, count),
         description: t('generic.feedback.confirm-delete-item', { name: translation(count) }, count),
         confirm: async () => {
            loadingDelete.value = true;
            if (!actions?.delete) throw Error('Delete method not provided');
            await Promise.allSettled(itemsToDelete.map((itemToDelete) => actions!.delete!(itemToDelete.id, itemToDelete)))
               .then(() => {
                  getData(lastUsedQuery.value);
                  notifySuccess({ title: t('generic.feedback.type-deleted', { type: translation(count) }, count) });
                  itemsToDelete.forEach((itemToDelete) => {
                     const index = (overview.value?.data || []).findIndex((d) => d.id === itemToDelete.id);
                     if (index > -1) overview.value?.data.splice(index, 1);
                  });
                  if (done) done();
               })
               .finally(() => {
                  loadingDelete.value = false;
               });
         },
      });
   }
   //#endregion

   return {
      overview,
      loading,
      loadingCreate,
      loadingChange,
      loadingDelete,

      lastUsedQuery,

      changeData,
      createData,
      deleteData,
      deleteMultipleData,
      getData,
   };
}
